import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Select,
  NumberInput,
  Button,
  Group,
  Stack,
  Alert,
  Text,
  Divider,
} from '@mantine/core';
import { AlertCircle, Archive } from 'react-feather';
import type { SubGroupWithTemplates, CohortStatus } from '../../../../types';
import { taskSubGroupsApi } from '../../../../services/api/taskSubGroups';

// Cohort status options for sub-group mapping (subset of all statuses per AC)
const COHORT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_design', label: 'In Design' },
  { value: 'in_prep', label: 'In Prep' },
  { value: 'live', label: 'Live' },
  { value: 'wrap_up', label: 'Wrap Up' },
];

interface SubGroupModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'add' | 'edit';
  phaseId: string;
  phaseName?: string;
  subGroup?: SubGroupWithTemplates | null;
  existingSubGroupCount?: number;
}

interface FormData {
  name: string;
  cohort_status: CohortStatus | null;
  sort_order: number;
}

export function SubGroupModal({
  opened,
  onClose,
  onSuccess,
  mode,
  phaseId,
  phaseName,
  subGroup,
  existingSubGroupCount = 0,
}: SubGroupModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    cohort_status: null,
    sort_order: existingSubGroupCount + 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateCount, setTemplateCount] = useState<number>(0);
  const [loadingTemplateCount, setLoadingTemplateCount] = useState(false);

  // Reset form when modal opens/closes or subGroup changes
  useEffect(() => {
    if (opened) {
      if (mode === 'edit' && subGroup) {
        setFormData({
          name: subGroup.name,
          cohort_status: subGroup.cohort_status || null,
          sort_order: subGroup.sort_order,
        });
        // Fetch template count for archive warning
        fetchTemplateCount(subGroup.id);
      } else {
        setFormData({
          name: '',
          cohort_status: null,
          sort_order: existingSubGroupCount + 1,
        });
        setTemplateCount(0);
      }
      setError(null);
    }
  }, [opened, mode, subGroup, existingSubGroupCount]);

  const fetchTemplateCount = async (subGroupId: string) => {
    setLoadingTemplateCount(true);
    try {
      const result = await taskSubGroupsApi.getTemplateCount(subGroupId);
      setTemplateCount(result.template_count);
    } catch {
      setTemplateCount(0);
    } finally {
      setLoadingTemplateCount(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.cohort_status) {
      setError('Cohort Status is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'add') {
        await taskSubGroupsApi.create({
          phase_id: phaseId,
          name: formData.name.trim(),
          cohort_status: formData.cohort_status,
          sort_order: formData.sort_order,
        });
      } else if (subGroup) {
        await taskSubGroupsApi.update(subGroup.id, {
          name: formData.name.trim(),
          cohort_status: formData.cohort_status,
          sort_order: formData.sort_order,
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!subGroup) return;

    setLoading(true);
    setError(null);

    try {
      await taskSubGroupsApi.archive(subGroup.id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive sub-group');
    } finally {
      setLoading(false);
    }
  };

  const canArchive = templateCount === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === 'add' ? 'Add Sub-Group' : 'Edit Sub-Group'}
      size="md"
    >
      <Stack gap="md">
        {/* Warning banner */}
        <Alert
          icon={<AlertCircle size={16} />}
          color="yellow"
          variant="light"
        >
          Changes affect task organization for all cohorts
        </Alert>

        {phaseName && (
          <Text size="sm" c="dimmed">
            Phase: <Text component="span" fw={600}>{phaseName}</Text>
          </Text>
        )}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <TextInput
          label="Title"
          placeholder="Enter sub-group title"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          styles={{ input: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' } }}
        />

        <Select
          label="Cohort Status"
          placeholder="Select cohort status"
          data={COHORT_STATUS_OPTIONS}
          value={formData.cohort_status}
          onChange={(value) => setFormData({ ...formData, cohort_status: value as CohortStatus | null })}
          required
          description="Tasks in this sub-group will be associated with this cohort status"
        />

        <NumberInput
          label="Sort Order"
          placeholder="Display order"
          value={formData.sort_order}
          onChange={(value) => setFormData({ ...formData, sort_order: typeof value === 'number' ? value : 1 })}
          min={1}
        />

        {/* Archive section for edit mode */}
        {mode === 'edit' && subGroup && !subGroup.is_archived && (
          <>
            <Divider my="sm" />
            <Stack gap="xs">
              <Text size="sm" fw={500}>Archive Sub-Group</Text>
              {!canArchive ? (
                <Alert
                  icon={<AlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  Cannot archive: {loadingTemplateCount ? 'Loading...' : `${templateCount} task template(s) exist in this sub-group`}
                </Alert>
              ) : (
                <Button
                  variant="light"
                  color="orange"
                  leftSection={<Archive size={16} />}
                  onClick={handleArchive}
                  loading={loading}
                >
                  Archive Sub-Group
                </Button>
              )}
            </Stack>
          </>
        )}

        {/* Footer buttons */}
        <Divider my="xs" />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {mode === 'add' ? 'Add Sub-Group' : 'Save Changes'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default SubGroupModal;
